import { DialogTitle } from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import Select from "components/Select";
import Button from "components/buttons/Button";
import TextAreaFormField from "components/form/TextAreaFormField";
import Modal from "components/modal/Modal";
import { Calendar } from "components/ui/calendar";
import { TimePickerInput } from "components/ui/time-picker-input";
import { Clock } from "lucide-react";
import { useRejectOfferForm } from "pages/project/offer/hooks/useRejectOfferForm";
import { OfferListType, RejectReason } from "pages/project/offer/offer.typing";
import { useOffer, useOfferRejectionReason } from "query/offer.query";
import { useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface Props {
  show: boolean;
  filterType: OfferListType;
  offerId?: string;
}

const DeclineOfferModal = ({ show, filterType, offerId }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "offer.modal.decline" });

  const [month] = useState(new Date());

  const { isLoading: isOfferLoading, selectedOffer } = useOffer(offerId);
  const { data: rejectReasons = [], isLoading } = useOfferRejectionReason();

  const { isRejectReasonShortDeadline, reject, reset, control, register, formErrors } = useRejectOfferForm(
    offerId ?? "",
    filterType
  );
  const minuteRef = useRef<HTMLInputElement>(null);
  const hourRef = useRef<HTMLInputElement>(null);

  if (isOfferLoading || !selectedOffer) {
    return null;
  }
  return (
    <Modal isShown={show} onClose={reset}>
      <div>
        {/*Icon*/}
        <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-red-100">
          <ExclamationTriangleIcon className="size-6 text-red-600" aria-hidden="true" />
        </div>
        {/*Header*/}
        <div className="mt-3 text-center sm:mt-5">
          <DialogTitle as="h3" className="text-color-primary text-lg font-medium leading-6 sm:text-3xl">
            <div>{t("title", { count: 1 })}</div>
            <div>{selectedOffer.taskCode}</div>
          </DialogTitle>
          <div className="mt-6 flex flex-col text-center">
            <div className="text-base font-medium">{t("subTitle")}</div>
          </div>
        </div>

        {/*body*/}
        <div className="mx-4 mt-6">
          <form className="mx-4 mt-6 flex flex-col space-y-5" onSubmit={reject}>
            <Controller
              name="reason"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select<RejectReason>
                  label={t("form.reason")}
                  items={rejectReasons}
                  selectedItem={value}
                  error={formErrors.reason?.id?.message}
                  onSelection={onChange}
                  renderName={(value) => (value?.label ? value.label : t("form.emptyReason"))}
                />
              )}
            />
            {isRejectReasonShortDeadline ? (
              <>
                <Controller
                  name="newDeadline"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <div className="mt-5">
                      <label className="text-color-secondary block font-sans text-sm font-medium">
                        {t("form.newDeadline")}
                      </label>

                      <div className="flex flex-col items-center">
                        <Calendar
                          autoFocus
                          mode="single"
                          startMonth={month}
                          defaultMonth={month}
                          selected={value}
                          onSelect={onChange}
                        />
                        <div className="flex flex-row items-center gap-2">
                          <div className="grid gap-1 text-center">
                            <label htmlFor="hours" className="text-color-secondary block font-sans text-xs font-medium">
                              {t("form.hours")}
                            </label>
                            <TimePickerInput
                              picker="hours"
                              date={value}
                              setDate={onChange}
                              ref={hourRef}
                              onRightFocus={() => minuteRef.current?.focus()}
                            />
                          </div>
                          <div className="grid gap-1 text-center">
                            <label
                              htmlFor="minutes"
                              className="text-color-secondary block font-sans text-xs font-medium"
                            >
                              {t("form.minutes")}
                            </label>
                            <TimePickerInput
                              picker="minutes"
                              date={value}
                              setDate={onChange}
                              ref={minuteRef}
                              onLeftFocus={() => hourRef.current?.focus()}
                            />
                          </div>
                          <div className="mt-5">
                            <Clock className="text-color-secondary size-5" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                />
              </>
            ) : null}
            <TextAreaFormField
              id="comment"
              label={t("form.comment")}
              className="max-h-52"
              {...register("comment")}
              error={formErrors.comment?.message}
            />
            <div className="mt-5 grid grid-cols-2 gap-3 sm:mt-6 sm:grid-flow-row-dense">
              <Button
                type="reset"
                disabled={isLoading}
                color="ternary"
                className="w-full"
                label={t("cancel")}
                onClick={reset}
              />
              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 sm:col-start-2"
              >
                {t("confirm")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default DeclineOfferModal;
